import React from 'react';
import Icon from '../shared/Icon';
import './OutputViewResult.css';

const isPlainObject = (v) => v != null && typeof v === 'object' && !Array.isArray(v);

const isFhir = (result) => isPlainObject(result) && typeof result.resourceType === 'string';

// A field-list-shaped view: an array of fields, or a { fields: [...] } wrapper.
const isFieldListShape = (result) =>
  Array.isArray(result) ||
  (isPlainObject(result) && (Array.isArray(result.fields) || Array.isArray(result.field_list)));

// "resourceType" → "Resource Type", "patient_name" → "Patient Name".
const humanizeKey = (key) =>
  String(key)
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());

const headingFor = (result) => {
  if (isFhir(result)) return `${result.resourceType}${result.status ? ` · ${result.status}` : ''}`;
  if (isFieldListShape(result)) return 'Field List';
  return 'Formatted output';
};

/** Render a single value: primitives inline, complex values as compact JSON. */
function ValueCell({ value }) {
  if (value == null || value === '') {
    return <span className="ovr-value ovr-value--empty">—</span>;
  }
  if (Array.isArray(value)) {
    const allPrimitive = value.every((v) => v == null || typeof v !== 'object');
    if (allPrimitive) {
      return <span className="ovr-value">{value.filter((v) => v != null).join(', ')}</span>;
    }
    return <pre className="ovr-json">{JSON.stringify(value, null, 2)}</pre>;
  }
  if (isPlainObject(value)) {
    return <pre className="ovr-json">{JSON.stringify(value, null, 2)}</pre>;
  }
  return <span className="ovr-value">{String(value)}</span>;
}

/**
 * Normalize the field-list shapes into flat label/value rows: a field-list
 * array, a { fields: [...] } wrapper, or a plain object whose own keys become
 * rows.
 */
function toFieldRows(result) {
  if (Array.isArray(result)) {
    return result.map((item, i) => {
      if (isPlainObject(item)) {
        const label = item.label ?? item.name ?? item.key;
        if (label != null && 'value' in item) return { label: String(label), value: item.value };
        const entries = Object.entries(item);
        if (entries.length === 1) return { label: humanizeKey(entries[0][0]), value: entries[0][1] };
        return { label: `#${i + 1}`, value: item };
      }
      return { label: `#${i + 1}`, value: item };
    });
  }
  const nested = result.fields || result.field_list;
  if (Array.isArray(nested)) return toFieldRows(nested);
  return Object.entries(result).map(([k, v]) => ({ label: humanizeKey(k), value: v }));
}

/** Serialize a value for copy/paste, mirroring how ValueCell displays it. */
function valueToText(value) {
  if (value == null || value === '') return '';
  if (Array.isArray(value)) {
    const allPrimitive = value.every((v) => v == null || typeof v !== 'object');
    return allPrimitive ? value.filter((v) => v != null).join(', ') : JSON.stringify(value);
  }
  if (isPlainObject(value)) return JSON.stringify(value);
  return String(value);
}

/**
 * Plain-text rendering of a filled output view, for the copy button and report
 * preview. FHIR is inherently a nested document, so it copies as canonical
 * pretty-printed JSON; field-list/generic views copy as the "Label: value"
 * lines the card shows.
 */
export function outputViewResultToText(result) {
  if (result == null || typeof result !== 'object') return '';
  if (isFhir(result)) return JSON.stringify(result, null, 2);
  const rows = toFieldRows(result);
  const body = rows.map((r) => `${r.label}: ${valueToText(r.value)}`).join('\n');
  return body ? `${headingFor(result)}\n${body}` : JSON.stringify(result, null, 2);
}

/**
 * Renders a backend-filled output view (`output_view_result`) as a card. FHIR
 * (has `resourceType`) is a nested document, so it renders as a pretty-printed
 * JSON block under the resource title. Field-list/generic views render as a
 * label/value list; empty objects fall back to JSON.
 *
 * @param {Object} props
 * @param {Object} props.result - The filled view object
 */
function OutputViewResult({ result }) {
  if (result == null || typeof result !== 'object') return null;

  const fhir = isFhir(result);
  const heading = headingFor(result);
  const rows = fhir ? [] : toFieldRows(result);

  return (
    <div className={`resui-card ovr-card${fhir ? ' ovr-card--fhir' : ''}`}>
      <div className="ovr-header">
        <Icon name={fhir ? 'medical_information' : 'list_alt'} className="ovr-icon" size={16} />
        <span className="ovr-title">{heading}</span>
      </div>
      {fhir || rows.length === 0 ? (
        <pre className="ovr-json ovr-json--doc">{JSON.stringify(result, null, 2)}</pre>
      ) : (
        <dl className="ovr-fields">
          {rows.map((row, i) => (
            <div className="ovr-row" key={`${row.label}-${i}`}>
              <dt className="ovr-label">{row.label}</dt>
              <dd className="ovr-value-cell"><ValueCell value={row.value} /></dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  );
}

export default OutputViewResult;
